import { useMemo, useCallback } from 'react';
import { useDeliveryLocation } from './useDeliveryLocation';

export const useCarbonFootprint = (submodel) => {
  const elements = submodel?.submodelElements || [];

  // Wrap the recursive finder in useCallback so it's stable across renders
  const findValue = useCallback((array, idShort) => {
    if (!array) return null;

    for (const el of array) {
      if (el.idShort === idShort) return el.value;

      if (el.value) {
        const found = findValue(el.value, idShort);
        if (found !== null) return found;
      }
    }

    return null;
  }, []);

  const tcfElements = elements.filter(el => el.idShort?.startsWith('TransportCarbonFootprint'));
  const pcfElements = elements.filter(el => el.idShort?.startsWith('ProductCarbonFootprint'));

  const actualQuantityStr =
    findValue(elements, 'Quantity') ||
    findValue(elements, 'OrderQuantity') ||
    findValue(elements, 'BatchSize');
  const actualQuantity = actualQuantityStr ? parseFloat(actualQuantityStr) : 1;

  // Scaled Product Carbon Footprint
  const scaledPCF = useMemo(() => {
    let totalIntensity = 0;
    let hasData = false;

    pcfElements.forEach(block => {
      const co2eq = parseFloat(findValue(block.value, 'PCFCO2eq') || 0);
      const qtyStr = findValue(block.value, 'PCFQuantityOfMeasureForCalculation');
      const qty = parseFloat(qtyStr) || 0;

      if (co2eq > 0 && qty > 0) {
        totalIntensity += co2eq / qty;
        hasData = true;
      }
    });

    if (!hasData) {
      return { total: 0, unit: 'kg CO₂eq', per: '' };
    }

    return {
      total: totalIntensity * actualQuantity,
      unit: 'kg CO₂eq',
      per: `sum of ${pcfElements.length} lifecycle stage${pcfElements.length > 1 ? 's' : ''}`,
    };
  }, [pcfElements, actualQuantity, findValue]);

  // Scaled Transport Carbon Footprint
  const scaledTCF = useMemo(() => {
    let totalIntensity = 0;

    tcfElements.forEach(block => {
      const co2eq = parseFloat(findValue(block.value, 'TCFCO2eq') || 0);
      const qtyStr = findValue(block.value, 'TCFQuantityOfMeasureForCalculation');
      const qty = parseFloat(qtyStr) || 0;

      if (co2eq > 0 && qty > 0) {
        totalIntensity += co2eq / qty;
      }
    });

    if (totalIntensity === 0) {
      return { total: 0, unit: 'kg CO₂eq', per: '' };
    }

    return {
      total: totalIntensity * actualQuantity,
      unit: 'kg CO₂eq',
      per: 'all transport steps',
    };
  }, [tcfElements, actualQuantity, findValue]);

  // Latest transport step (for delivery location)
  const latestPCF = useMemo(() => {
    if (pcfElements.length === 0) return null;

    return pcfElements.reduce((latest, cur) => {
      const a = parseInt((latest.idShort || '').replace(/\D/g, ''), 10) || 0;
      const b = parseInt((cur.idShort || '').replace(/\D/g, ''), 10) || 0;
      return b > a ? cur : latest;
    });
  }, [pcfElements]);

  // Latest transport step (for delivery location)
  const latestTCF = useMemo(() => {
    if (tcfElements.length === 0) return null;

    return tcfElements.reduce((latest, cur) => {
      const a = parseInt((latest.idShort || '').replace(/\D/g, ''), 10) || 0;
      const b = parseInt((cur.idShort || '').replace(/\D/g, ''), 10) || 0;
      return b > a ? cur : latest;
    });
  }, [tcfElements]);

  const { deliveryAddress, mapUrl, publicationDate } = useDeliveryLocation(latestPCF, latestTCF);

  return {
    actualQuantity,
    scaledPCF,
    scaledTCF,
    deliveryAddress,
    mapUrl,
    transportStepsCount: tcfElements.length,
    publicationDate
  };
};